import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { useUserStore } from "store/userStore";
import { isUserSideRole } from "utils/roles";
import { getWinnerBadges } from "apis/adminServices/giveawayServices";
import { ContestWinnerItem, ContestWinnerBadgeGroup } from "types/giveaway";
import GiveawayWinnerModal from "components/GiveawayWinnerModal";
import { toDate } from "pages/Admin/giveawayManagement/giveawayFormUtils";

const DISMISS_KEY = "giveawayWinnerBannerDismissed";

/**
 * Top-of-page announcement banner for active giveaway winners. Uses the same
 * winner badge API, active-window filtering and winner modal that the sidebar
 * card previously used.
 */
const GiveawayWinnerBanner: React.FC = () => {
  const userRole = useUserStore((state) => state.role);

  const [activeWinners, setActiveWinners] = useState<ContestWinnerItem[]>([]);
  const [activeWinnerGroups, setActiveWinnerGroups] = useState<
    ContestWinnerBadgeGroup[]
  >([]);
  const [isWinnerModalOpen, setIsWinnerModalOpen] = useState(false);
  const [isDismissed, setIsDismissed] = useState(
    () => sessionStorage.getItem(DISMISS_KEY) === "1",
  );
  const bannerRef = useRef<HTMLDivElement>(null);

  // Fetch winner badges for the banner
  useEffect(() => {
    if (!isUserSideRole(userRole)) return;
    let active = true;
    getWinnerBadges()
      .then((groups) => {
        if (!active) return;
        if (groups && groups.length > 0) {
          const now = Date.now();
          const isActiveWindow = (w: ContestWinnerItem) => {
            if (!w.displayFrom || !w.displayTo) return false;
            const fromDate = toDate(w.displayFrom);
            const toDateObj = toDate(w.displayTo);
            if (!fromDate || !toDateObj) return false;
            return fromDate.getTime() <= now && now <= toDateObj.getTime();
          };

          const currentGroups = groups
            .map((g) => ({
              ...g,
              winners: Array.isArray(g.winners)
                ? g.winners.filter(isActiveWindow)
                : [],
            }))
            .filter((g) => g.winners.length > 0);

          setActiveWinnerGroups(currentGroups);
          setActiveWinners(currentGroups.flatMap((g) => g.winners));
        } else {
          setActiveWinnerGroups([]);
          setActiveWinners([]);
        }
      })
      .catch((err) => {
        console.error("Failed to load winner badges:", err);
      });

    return () => {
      active = false;
    };
  }, [userRole]);

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setIsDismissed(true);
  };

  const isVisible = !isDismissed && activeWinners.length > 0;

  // The banner sits above the whole app shell (sidebar included), so the shell
  // and the fixed sidebar shift down by exactly its height. Height is measured
  // (not hardcoded) because on narrow/mobile screens the content wraps to two
  // lines, and a fixed height would clip the bottom row so it overlaps the
  // header below it.
  useLayoutEffect(() => {
    if (!isVisible) {
      document.documentElement.style.setProperty("--giveaway-banner-h", "0px");
      return;
    }

    const el = bannerRef.current;
    if (!el) return;

    const updateHeight = () => {
      document.documentElement.style.setProperty(
        "--giveaway-banner-h",
        `${el.offsetHeight}px`,
      );
    };

    updateHeight();
    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(el);

    return () => {
      resizeObserver.disconnect();
      document.documentElement.style.setProperty("--giveaway-banner-h", "0px");
    };
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <>
      <div
        ref={bannerRef}
        className="fixed inset-x-0 top-0 z-50 flex min-h-[56px] items-center overflow-hidden bg-gradient-to-r from-[#0B0705] via-[#2A1A06] to-[#0B0705] px-4 py-2 pr-10 sm:py-0 sm:px-6 sm:pr-12"
      >
        {/* Gold glow accents */}
        <div className="pointer-events-none absolute -top-10 left-1/4 h-24 w-40 rounded-full bg-amber-400/20 blur-2xl" />
        <div className="pointer-events-none absolute -bottom-10 right-1/4 h-24 w-40 rounded-full bg-amber-500/10 blur-2xl" />

        <div className="relative z-10 flex w-full flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center">
          <span className="text-[22px] font-black uppercase tracking-wide sm:text-[29px]">
            <span className="bg-gradient-to-b from-[#FDE68A] to-[#D19A26] bg-clip-text text-transparent">
              Giveaway{" "}
            </span>
            <span className="text-white">Winners</span>
          </span>
          <span className="hidden h-6 w-0.5 bg-gradient-to-b from-[#FDE68A] to-[#D19A26] sm:block" />
          <span className="text-md text-white sm:text-base">
            Congratulations!
          </span>
          <button
            type="button"
            onClick={() => setIsWinnerModalOpen(true)}
            className="inline-flex items-center gap-1 rounded-xl bg-gradient-to-b from-[#FDE68A] to-[#D19A26] px-3 py-1.5 text-[11px] font-bold text-black transition-colors hover:from-[#FBBF24] hover:to-[#B87F1A] sm:text-xs"
          >
            Contest Winners
            <svg
              className="h-3.5 w-3.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>

        <button
          type="button"
          aria-label="Close giveaway winners banner"
          onClick={handleDismiss}
          className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-md p-1.5 text-white/70 transition-colors hover:bg-white/10 hover:text-white sm:right-4"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Giveaway Winner Announcement Modal */}
      <GiveawayWinnerModal
        isOpen={isWinnerModalOpen}
        onClose={() => setIsWinnerModalOpen(false)}
        groups={activeWinnerGroups}
      />
    </>
  );
};

export default GiveawayWinnerBanner;
